import { useQueryClient, useMutation } from '@tanstack/react-query';
import { apiPatch } from '@/lib/api';
import type { Task, CustomField } from '@/lib/schemas';

// ============================================
// CALENDAR HOOKS
// ============================================
//
// t16: small helper hooks dedicated to the Calendar view. They sit
// alongside use-tasks.ts / use-views.ts (which own the canonical task
// and saved-view queries) and only own mutations / derivations that the
// Calendar view needs:
//
//   - updateTaskDate — PATCH /api/tasks/:id with the right field based
//     on the active view's `dateSource`. Optimistic write on every
//     matching query key with rollback on failure (mirrors the
//     Kanban card-move pattern in kanban-view.tsx).
//
//   - getSprintBands — pure helper to extract the sprint list from a
//     project's Sprint-typed custom fields. Kept here so calendar-day-
//     cell.tsx, calendar-month.tsx and calendar-week.tsx all share one
//     implementation.

// ============================================
// TYPES
// ============================================

// Mirrors the same shape the backend writes into CustomField.options
// for SPRINT-typed fields (see t14 in schema.prisma).
//   { sprints: [{ id, name, startDate, endDate, color? }, ...] }
// `startDate` / `endDate` are ISO date strings (YYYY-MM-DD or full
// ISO datetimes — the consumer parses them with `new Date(...)`).
export interface SprintDef {
    id: string;
    name: string;
    startDate: string;
    endDate: string;
    color?: string;
}

export interface SprintBandsPayload {
    field: CustomField | null;
    sprints: SprintDef[];
}

// ============================================
// SPRINT LOOKUP
// ============================================
//
// Resolves the project's Sprint-typed custom field(s) and returns the
// parsed sprint list. CustomField.options is typed as
// `CustomFieldOption[]` in the shared schema, but SPRINT fields store
// their payload as `{ sprints: SprintDef[] }`. We try the structured
// shape first and fall back to an empty array so the rest of the
// Calendar view can assume a clean typed shape.

export function getSprintBands(customFields: CustomField[] | undefined): SprintBandsPayload {
    if (!customFields || customFields.length === 0) {
        return { field: null, sprints: [] };
    }
    const sprintField = customFields.find((f) => f.type === 'SPRINT');
    if (!sprintField) return { field: null, sprints: [] };

    const raw = sprintField.options;
    let sprints: SprintDef[] = [];

    if (Array.isArray(raw)) {
        // Legacy / flat shape — no structured sprints, just labelled options.
        sprints = [];
    } else if (raw && typeof raw === 'object') {
        // The structured shape is `sprints: SprintDef[]` stored inside
        // an object. Some writers may store it directly as the array;
        // we accept both.
        const obj = raw as Record<string, unknown>;
        const maybe = Array.isArray(obj.sprints) ? obj.sprints : Array.isArray(raw) ? raw : [];
        sprints = (maybe as unknown[])
            .map((entry): SprintDef | null => {
                if (!entry || typeof entry !== 'object') return null;
                const e = entry as Record<string, unknown>;
                if (typeof e.id !== 'string' || typeof e.name !== 'string') return null;
                if (typeof e.startDate !== 'string' || typeof e.endDate !== 'string') return null;
                const color = typeof e.color === 'string' ? e.color : undefined;
                return {
                    id: e.id,
                    name: e.name,
                    startDate: e.startDate,
                    endDate: e.endDate,
                    color,
                };
            })
            .filter((s): s is SprintDef => s !== null);
    }

    return { field: sprintField, sprints };
}

// ============================================
// SPRINT-COLOUR LOOKUP
// ============================================
//
// Resolve the colour to use for a task's pill when `colorBy` is set to
// a SPRINT-typed custom field id. Returns null when the task isn't
// associated with any sprint or the sprint has no colour.

export function getSprintColourForTask(
    task: Task,
    sprintFieldId: string | undefined,
    sprints: SprintDef[]
): string | null {
    if (!sprintFieldId) return null;
    const rawValue = task.customFieldValues?.[sprintFieldId];
    if (rawValue == null) return null;
    const sprintId = typeof rawValue === 'string' ? rawValue : String(rawValue);
    const match = sprints.find((s) => s.id === sprintId);
    return match?.color ?? null;
}

// ============================================
// TASK-DATE MUTATION
// ============================================
//
// Optimistic PATCH that updates whichever date field the active view's
// `dateSource` points at. Used by drag-to-update inside the calendar.
// Sprint dates are managed via the Sprint custom field value (PATCH
// /api/tasks/:id with `customFieldValues[sprintFieldId] = sprintId`)
// so for `sprintStart` / `sprintEnd` we don't mutate a Task column —
// we mutate the Sprint custom field value (the user can re-pick the
// sprint for the task via the TaskDialog instead of dragging).

export type CalendarDateSource =
    | 'dueDate'
    | 'startDate'
    | 'endDate'
    | 'sprintStart'
    | 'sprintEnd';

export interface UpdateTaskDateInput {
    taskId: string;
    /** ISO date string (YYYY-MM-DD or full ISO). */
    newDate: string | null;
    /** Optional sprint id when the date source is sprintStart/sprintEnd. */
    sprintId?: string | null;
    /** Required when the date source is sprintStart/sprintEnd. */
    sprintFieldId?: string;
    projectId: string;
    /** The active view's dateSource (defaults to 'dueDate'). T21 R4
     *  also accepts the display-only 'all' sentinel — the hook
     *  treats it the same as 'dueDate' since drag-to-update with no
     *  primary date column doesn't have a meaningful target. */
    dateSource?: import('@/components/tasks/calendar/calendar-config').CalendarDateSourceWithAll;
}

export function useUpdateTaskDate(projectId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (input: UpdateTaskDateInput) => {
            const dateSource: import('@/components/tasks/calendar/calendar-config').CalendarDateSourceWithAll =
                input.dateSource ?? 'dueDate';

            // For sprintStart / sprintEnd we don't have a real Task column
            // to set — the sprint "date" lives on the Sprint custom field's
            // sprint record. The closest user-meaningful action we can do
            // from a drag is to assign the task to one of the sprints that
            // contains the drop day. The Calendar view passes the resolved
            // sprintId via input.sprintId. Fallback: clear it (null).
            if (dateSource === 'sprintStart' || dateSource === 'sprintEnd') {
                if (!input.sprintFieldId) {
                    throw new Error('Sprint field id is required for sprintStart/sprintEnd');
                }
                const customFieldValues: Record<string, unknown> = {
                    [input.sprintFieldId]: input.sprintId ?? null,
                };
                return apiPatch<Task>(`/tasks/${input.taskId}`, { customFieldValues });
            }

            // Built-in date columns.
            const field = dateSource; // 'dueDate' | 'startDate' | 'endDate'
            const payload: Record<string, unknown> = { [field]: input.newDate };
            return apiPatch<Task>(`/tasks/${input.taskId}`, payload);
        },
        // Optimistic update + rollback. Mirrors the Kanban card-move
        // pattern (see useReorderColumns in use-views.ts for the
        // setQueryData sweep). We snapshot every ['tasks', 'list',
        // projectId, ...] cache variant so any in-flight page can roll
        // back independently.
        onMutate: async (input) => {
            const dateSource: import('@/components/tasks/calendar/calendar-config').CalendarDateSourceWithAll =
                input.dateSource ?? 'dueDate';
            const queryKeys = queryClient
                .getQueryCache()
                .findAll({ queryKey: ['tasks', 'list', projectId] })
                .map((q) => q.queryKey);

            const snapshot = new Map<string, unknown>();
            for (const key of queryKeys) {
                snapshot.set(JSON.stringify(key), queryClient.getQueryData(key));
            }

            for (const key of queryKeys) {
                queryClient.setQueryData(key, (old: any) => {
                    if (!old?.pages) return old;
                    return {
                        ...old,
                        pages: old.pages.map((page: any) => ({
                            ...page,
                            tasks: page.tasks.map((t: Task) => {
                                if (t.id !== input.taskId) return t;
                                if (dateSource === 'sprintStart' || dateSource === 'sprintEnd') {
                                    if (!input.sprintFieldId) return t;
                                    return {
                                        ...t,
                                        customFieldValues: {
                                            ...(t.customFieldValues || {}),
                                            [input.sprintFieldId]: input.sprintId ?? null,
                                        },
                                    };
                                }
                                const field = dateSource;
                                return { ...t, [field]: input.newDate };
                            }),
                        })),
                    };
                });
            }

            return { snapshot, queryKeys };
        },
        onError: (_err, _input, context) => {
            if (!context) return;
            for (const [json, data] of context.snapshot) {
                queryClient.setQueryData(JSON.parse(json), data);
            }
        },
        onSettled: () => {
            // Re-fetch so the server's view of completedAt / etc stays
            // in sync with what we just rendered optimistically.
            queryClient.invalidateQueries({ queryKey: ['tasks', 'list', projectId] });
            queryClient.invalidateQueries({ queryKey: ['tasks', 'my'] });
        },
    });
}
